import { useEffect, useState } from 'react';
import { cashEntriesApi } from '../lib/api/cash-entries';
import { formatVND } from '../lib/format';

interface Props {
  open: boolean;
  currentBalance: number;
  method: 'cash_1111' | 'bank_1121';
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Kiểm kê quỹ tiền mặt: NV nhập tổng số tiền thực tế đếm được → so sánh với
 * số dư sổ sách → nếu lệch, auto tạo phiếu điều chỉnh (PT nếu thừa, PC nếu thiếu).
 */
export function KiemKeQuyModal({ open, currentBalance, method, onClose, onSaved }: Props) {
  const [countedInput, setCountedInput] = useState('');
  const [note, setNote] = useState('');
  const [countedAt, setCountedAt] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCountedInput(currentBalance.toLocaleString('vi-VN'));
    setNote('Kiểm kê quỹ định kỳ');
    setCountedAt(new Date().toISOString().slice(0, 10));
    setErr(null); setSaving(false);
  }, [open, currentBalance]);

  if (!open) return null;

  const counted = Number(countedInput.replace(/\D/g, '')) || 0;
  const diff = counted - currentBalance;
  const isExact = diff === 0;
  const isOver = diff > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (counted < 0) return setErr('Số tiền thực tế không thể âm.');

    if (isExact) {
      // Không cần tạo phiếu — chỉ đóng modal
      onSaved();
      return;
    }

    setSaving(true);
    try {
      const now = new Date();
      const dstr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
      const prefix = method === 'cash_1111'
        ? (isOver ? 'PT' : 'PC')
        : 'UNC';
      const rnd = String(Math.floor(Math.random() * 999)).padStart(3, '0');
      await cashEntriesApi.create({
        code: `${prefix}-${dstr}-KKQ${rnd}`,
        entryType: isOver ? 'receipt' : 'payment',
        method,
        entryDate: countedAt,
        description: `Kiểm kê quỹ ${countedAt}: ${isOver ? 'Thừa' : 'Thiếu'} ${formatVND(Math.abs(diff))}. ${note}`,
        // 3388/1381 — chênh lệch kiểm kê chờ xử lý
        offsetAccount: isOver ? '3388' : '1381',
        actor: 'Chênh lệch kiểm kê',
        amount: Math.abs(diff),
      });
      onSaved();
    } catch (e2) { setErr(e2 instanceof Error ? e2.message : String(e2)); }
    finally { setSaving(false); }
  };

  const methodLabel = method === 'cash_1111' ? 'Tiền mặt · 1111' : 'Tiền gửi NH · 1121';

  return (
    <div className="cvm-backdrop" onClick={onClose}>
      <div className="cvm-modal afm-modal" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="cvm-head">
            <div>
              <div className="cvm-crumb">KẾ TOÁN › SỔ QUỸ › KIỂM KÊ</div>
              <h2>Kiểm kê quỹ {methodLabel}</h2>
              <p>Đối chiếu số thực tế đếm được với số dư sổ sách. Chênh lệch sẽ auto tạo phiếu điều chỉnh.</p>
            </div>
            <button type="button" className="cvm-close" onClick={onClose}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg></button>
          </div>
          <div className="cvm-body">
            <div className="kkq-stat-grid">
              <div className="kkq-stat">
                <div className="kkq-stat-label">Sổ sách</div>
                <div className="kkq-stat-value">{formatVND(currentBalance)}</div>
              </div>
              <div className="kkq-stat">
                <div className="kkq-stat-label">Thực tế đếm</div>
                <div className="kkq-stat-value">{formatVND(counted)}</div>
              </div>
              <div className={`kkq-stat kkq-stat-diff ${isExact ? 'ok' : isOver ? 'over' : 'under'}`}>
                <div className="kkq-stat-label">Chênh lệch</div>
                <div className="kkq-stat-value">
                  {isExact ? '✓ Khớp' : `${isOver ? '+' : ''}${formatVND(diff)}`}
                </div>
              </div>
            </div>

            <div className="afm-grid-2">
              <div className="pfm-field">
                <label>Ngày kiểm kê <span className="req">*</span></label>
                <input type="date" value={countedAt} onChange={(e) => setCountedAt(e.target.value)} required />
              </div>
              <div className="pfm-field">
                <label>Số tiền thực tế đếm <span className="req">*</span></label>
                <input
                  inputMode="numeric"
                  value={counted ? counted.toLocaleString('vi-VN') : ''}
                  onChange={(e) => setCountedInput(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="pfm-field">
              <label>Ghi chú</label>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Lý do chênh lệch, người kiểm kê, chứng kiến…" />
            </div>

            {!isExact && (
              <div className="pfm-warn pfm-warn-yellow">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#a16207" strokeWidth="2" strokeLinecap="round"><path d="M12 8v4M12 16h.01" /><circle cx="12" cy="12" r="10" /></svg>
                <div>
                  Chênh lệch {formatVND(Math.abs(diff))} sẽ tạo phiếu {isOver ? 'thu' : 'chi'} với TK đối ứng
                  {' '}<b>{isOver ? '3388 (Phải trả khác)' : '1381 (Phải thu khác — chờ xử lý)'}</b>.
                  KTT sẽ điều chỉnh sau khi truy tìm nguyên nhân.
                </div>
              </div>
            )}

            {err && <div className="cvm-error">{err}</div>}
          </div>
          <div className="cvm-foot">
            <button type="button" className="pay-btn-outline" onClick={onClose} disabled={saving}>Huỷ</button>
            <button type="submit" className={isExact ? 'pay-btn-primary' : 'pay-btn-dark'} disabled={saving}>
              {saving ? 'Đang xử lý…' : isExact ? 'Xác nhận khớp — đóng' : `Tạo phiếu điều chỉnh ${formatVND(Math.abs(diff))}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
